import { useState, useEffect } from 'react';
import { Product } from '@/data/products';
import { fetchTopSellingProducts, fetchBestSellerProducts } from '@/services/productService';

interface UseTopProductsOptions {
  limitCount?: number;
  enabled?: boolean;
}

interface UseTopProductsResult {
  products: Product[];
  loading: boolean;
  error: string | null;
}

/**
 * Hook to fetch top selling products with fallback to best sellers
 * Optimized for hero section and featured products components
 */
export const useTopProducts = ({ 
  limitCount = 10, 
  enabled = true 
}: UseTopProductsOptions = {}): UseTopProductsResult => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    const loadTopProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // First try to get top selling products based on sales count
        let topProducts = await fetchTopSellingProducts(limitCount);
        
        // If no products have sales data, fallback to best seller products
        if (topProducts.length === 0 || (topProducts[0]?.salesCount || 0) === 0) {
          topProducts = await fetchBestSellerProducts(limitCount);
        }
        
        setProducts(topProducts);
      } catch (err) {
        setError('Failed to load top products');
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    loadTopProducts();
  }, [limitCount, enabled]);

  return { products, loading, error };
}; 